/**
 * workspace-configuration.ts  (Sprint BOOT-01)
 *
 * Canonical versioned Workspace Configuration.
 * Replaces fragmented, un-orchestrated localStorage keys with a unified,
 * cryptographically traceable configuration record.
 */

export interface InferencePolicy {
  localNeedleEnabled: boolean;
  remoteReasoningPolicy: 'disabled' | 'ask' | 'mesh-only' | 'any';
  meshServingEnabled: boolean;
  peerDelegationEnabled: boolean;
  batteryThrottleEnabled: boolean;
  thermalThrottleEnabled: boolean;
}

export interface ExternalIngressPolicy {
  enabled: boolean;
  allowedSources: readonly ('web-form' | 'email-bridge' | 'offline-qr' | 'guest-portal' | 'webhook')[];
  quarantineAll: boolean;
}

export interface ExternalEgressPolicy {
  enabled: boolean;
  requiresApproval: boolean;
  allowedDestinations: readonly ('email' | 'sms' | 'webhook' | 'guest-portal')[];
}

export interface RetentionPolicy {
  journalDays: number;
  mediaDays: number;
  archiveEnabled: boolean;
}

export interface DependencyDecision {
  pluginId: string;
  depPluginId: string;
  decision: 'accepted' | 'skipped';
  decidedAt: string;
  decidedBy: string;
}

export interface WorkspaceConfiguration {
  configVersion: number;
  workspaceId: string;
  workspaceName: string;
  timezone: string;
  locale: string;
  defaultPrivacyLevel: 'private' | 'workspace' | 'mesh';
  enabledPlugins: readonly string[];
  pluginSettings: Record<string, unknown>;
  policies: {
    inference: InferencePolicy;
    externalIngress: ExternalIngressPolicy;
    externalEgress: ExternalEgressPolicy;
    retention: RetentionPolicy;
  };
  dependencyDecisions: readonly DependencyDecision[];
  recoveryMethodsConfigured: number;
  setupStatus: 'draft' | 'review' | 'active';
  updatedAt: string;
  updatedBy: string;
}

export const DEFAULT_INFERENCE_POLICY: InferencePolicy = {
  localNeedleEnabled: true,
  remoteReasoningPolicy: 'ask',
  meshServingEnabled: false,
  peerDelegationEnabled: false,
  batteryThrottleEnabled: true,
  thermalThrottleEnabled: true,
};

export const DEFAULT_INGRESS_POLICY: ExternalIngressPolicy = {
  enabled: false,
  allowedSources: ['web-form', 'offline-qr'],
  quarantineAll: true,
};

export const DEFAULT_EGRESS_POLICY: ExternalEgressPolicy = {
  enabled: false,
  requiresApproval: true,
  allowedDestinations: ['email'],
};

export const DEFAULT_RETENTION_POLICY: RetentionPolicy = {
  journalDays: 90,
  mediaDays: 30,
  archiveEnabled: true,
};

export function createDefaultWorkspaceConfig(workspaceId: string, ownerId: string): WorkspaceConfiguration {
  return {
    configVersion: 1,
    workspaceId,
    workspaceName: 'Sovereign Workspace',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    locale: navigator?.language || 'en-US',
    defaultPrivacyLevel: 'workspace',
    enabledPlugins: ['ChatPlugin'],
    pluginSettings: {
      'CrmPlugin.configure-pipeline': true,
      'ActionPlannerPlugin.configure-workweek': true,
      'LLMWikiPlugin.configure-sources': true,
      'CommercePlugin.configure-currency': true,
      'InvoicingPlugin.configure-efris': true,
      'PosPlugin.configure-register': true,
      'WarehousePlugin.configure-facility': true,
      'DeliveryPlugin.configure-dispatch': true,
      'HardwarePlugin.configure-printer': true,
    },
    policies: {
      inference: { ...DEFAULT_INFERENCE_POLICY },
      externalIngress: { ...DEFAULT_INGRESS_POLICY },
      externalEgress: { ...DEFAULT_EGRESS_POLICY },
      retention: { ...DEFAULT_RETENTION_POLICY },
    },
    dependencyDecisions: [],
    recoveryMethodsConfigured: 0,
    setupStatus: 'draft',
    updatedAt: new Date().toISOString(),
    updatedBy: ownerId,
  };
}

export class LocalStorageWorkspaceConfigStorage {
  private static readonly KEY = 'workspace_config_v1';

  static load(workspaceId?: string): WorkspaceConfiguration | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(this.KEY);
      if (!raw) return null;
      const config = JSON.parse(raw) as WorkspaceConfiguration;
      if (workspaceId && config.workspaceId !== workspaceId) return null;
      return config;
    } catch {
      return null;
    }
  }

  static save(config: WorkspaceConfiguration): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(this.KEY, JSON.stringify(config));
    } catch {}
  }
}
